import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { Skeleton, Text } from '@musa.tech/design-system';
import { fmtWeightAxis, fmtWeightStr } from '@/utils/formatWeight';

interface MaterialBreakdown {
  name: string;
  weight: number;
}

interface MaterialData {
  group: string;
  weight: number;
  color: string;
  breakdown: MaterialBreakdown[];
}

interface IADistribuicaoChartProps {
  data: MaterialData[];
  isLoading?: boolean;
  error?: unknown;
}

const formatXAxis = (value: number) => fmtWeightAxis(value);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const weight = payload[0]?.value as number;
  const breakdown: MaterialBreakdown[] = payload[0]?.payload?.breakdown ?? [];
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-sm min-w-[160px]">
      <Text variant="label" size="small" className="text-foreground mb-1 block">{label}</Text>
      <Text variant="body" size="small" className="text-muted-foreground">
        Total: {fmtWeightStr(weight)}
      </Text>
      {breakdown.length > 1 && (
        <div className="mt-1.5 pt-1.5 border-t border-border space-y-0.5">
          {breakdown.map((b) => (
            <div key={b.name} className="flex justify-between gap-4">
              <Text variant="body" size="small" className="text-muted-foreground">{b.name}</Text>
              <Text variant="body" size="small" className="text-muted-foreground">{fmtWeightStr(b.weight)}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const IADistribuicaoChart = ({ data, isLoading, error }: IADistribuicaoChartProps) => {
  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-xl" />;
  }

  const isEmpty = !data || data.length === 0;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <Text variant="heading" size="large" className="text-foreground">
        Distribuição por tipo de resíduo
      </Text>
      <Text variant="body" size="small" className="text-muted-foreground mt-0.5 mb-4 block">
        Proporção de cada grupo no total coletado
      </Text>

      {error ? (
        <div className="flex flex-col items-center justify-center h-52 gap-2">
          <Text variant="body" size="small" className="text-error-500">
            Erro ao carregar dados: {String(error instanceof Error ? error.message : error)}
          </Text>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center h-52 text-muted-foreground gap-2">
          <span className="text-3xl">📊</span>
          <Text variant="body" size="small">Sem dados no período selecionado</Text>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 10, left: 10, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--neutral-200)" />
            <XAxis
              type="number"
              tickFormatter={formatXAxis}
              tick={{ fontSize: 11, fill: 'var(--neutral-500)' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey="group"
              tick={{ fontSize: 11, fill: 'var(--neutral-500)' }}
              tickLine={false}
              axisLine={false}
              width={110}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="weight" radius={[0, 4, 4, 0]} maxBarSize={28}>
              {data.map((entry) => (
                <Cell key={entry.group} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
